// jwt.io-style byte-field breakdown for Inspect token / Inspect challenge.
//
// A Privacy Pass token/challenge is one opaque binary blob (not dot-delimited
// text like a JWT), so we colour the base64 string itself in place, mapping
// each fixed-offset byte field onto the characters that encode it. base64 packs
// 6 bits per character, so a character whose 6-bit window straddles a field
// boundary (only happens at byte offsets not divisible by 3) is split with a
// two-colour gradient rather than forced into one field.
//
// The token layout (RFC 9578-style) is:
//   token_type u16 | nonce[32] | challenge_digest[32] | token_key_id[32]
//   | authenticator[Nk]
// The bound metadata lives in the separate extensions blob (a TLS vector of
// { type u16, value opaque<0..2^16-1> }); the Expiration extension (type 1) is
// the human-readable "payload" analogue.

import { isChallengeInfo, isTokenInfo } from "./guards";
import type { AfterHook, ChallengeInfo, ExtInfo, Hit, Segment, TokenInfo } from "./types";

const FIELD_COLORS: Record<string, string> = {
  token_type: "#f6821f",
  nonce: "#1a7f37",
  challenge_digest: "#0969da",
  token_key_id: "#8250df",
  authenticator: "#8c959f",
  ext_type: "#bf8700",
  ext_len: "#1b7c83",
  ext_value: "#bf3989",
  // challenge fields
  c_token_type: "#f6821f",
  c_len: "#57606a",
  issuer_name: "#0969da",
  redemption_context: "#8250df",
  origin_info: "#1a7f37",
  digest: "#bf3989",
  // extension-set fields (es_type matches ext_type for consistency)
  es_required: "#1b7c83",
  es_type: "#bf8700",
};

const NEUTRAL = "#8c959f";
const colOf = (s: Segment | null | undefined): string =>
  (s && FIELD_COLORS[s.key]) || NEUTRAL;

/** Decode base64 / base64url (padded or not) to bytes. */
function b64bytes(s: string): Uint8Array {
  if (!s) return new Uint8Array(0);
  s = s.replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4) s += "=";
  try {
    const bin = atob(s);
    const u = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
    return u;
  } catch {
    return new Uint8Array(0);
  }
}

/** Byte at index `i`, or 0 when out of range (callers pre-check bounds). */
const byteAt = (b: Uint8Array, i: number): number => b[i] ?? 0;

/** Pull a quoted key="..." parameter out of a header value. */
const headerParam = (raw: string, key: string): string => {
  const m = raw.match(new RegExp(`${key}\\s*=\\s*"([^"]*)"`, "i"));
  return m?.[1] ?? "";
};

/**
 * Pull token="..." / extensions="..." out of an Authorization header, or treat
 * the whole input as a bare base64 token.
 */
function parseHeaderParams(raw: string): { token: string; extensions: string } {
  raw = (raw || "").trim();
  const t = raw.match(/token\s*=\s*"([^"]*)"/i);
  const e = raw.match(/extensions\s*=\s*"([^"]*)"/i);
  if (t) return { token: t[1] ?? "", extensions: e?.[1] ?? "" };
  return { token: raw, extensions: "" };
}

/**
 * Pull challenge / extension-set / extensions out of a WWW-Authenticate header,
 * or treat the whole input as a bare base64 challenge value.
 */
function parseChallengeParams(
  raw: string,
): { challenge: string; extensionSet: string; extensions: string } {
  raw = (raw || "").trim();
  const challenge = headerParam(raw, "challenge");
  if (challenge) {
    return {
      challenge,
      extensionSet: headerParam(raw, "extension-set"),
      extensions: headerParam(raw, "extensions"),
    };
  }
  return { challenge: raw, extensionSet: "", extensions: "" };
}

/** Fixed-offset fields of the token blob. */
function tokenSegments(bytes: Uint8Array): Segment[] {
  if (bytes.length < 98) return [];
  return [
    { key: "token_type", label: "token_type", off: 0, len: 2 },
    { key: "nonce", label: "nonce", off: 2, len: 32 },
    { key: "challenge_digest", label: "challenge_digest", off: 34, len: 32 },
    { key: "token_key_id", label: "token_key_id", off: 66, len: 32 },
    { key: "authenticator", label: "authenticator (RSA signature)", off: 98, len: bytes.length - 98 },
  ];
}

/** Generic type/length/value walk of the extensions TLS vector. */
function extSegments(bytes: Uint8Array): Segment[] {
  const segs: Segment[] = [];
  if (bytes.length < 2) return segs;
  segs.push({ key: "ext_len", label: "extensions vector length", off: 0, len: 2 });
  let i = 2;
  while (i + 4 <= bytes.length) {
    const type = (byteAt(bytes, i) << 8) | byteAt(bytes, i + 1);
    const vlen = (byteAt(bytes, i + 2) << 8) | byteAt(bytes, i + 3);
    segs.push({ key: "ext_type", label: `extension_type = ${type}`, off: i, len: 2 });
    segs.push({ key: "ext_len", label: `value length = ${vlen}`, off: i + 2, len: 2 });
    if (i + 4 + vlen <= bytes.length)
      segs.push({ key: "ext_value", label: `value (type ${type})`, off: i + 4, len: vlen });
    i += 4 + vlen;
  }
  return segs;
}

/**
 * Fields of a TokenChallenge (RFC 9577):
 *   token_type u16 | issuer_name<u16> | redemption_context<u8> | origin_info<u16>
 */
function challengeSegments(bytes: Uint8Array): Segment[] {
  const segs: Segment[] = [];
  if (bytes.length < 4) return segs;
  segs.push({ key: "c_token_type", label: "token_type", off: 0, len: 2 });
  let i = 2;
  const inLen = (byteAt(bytes, i) << 8) | byteAt(bytes, i + 1);
  segs.push({ key: "c_len", label: `issuer_name length = ${inLen}`, off: i, len: 2 });
  i += 2;
  if (inLen > 0) segs.push({ key: "issuer_name", label: "issuer_name", off: i, len: inLen });
  i += inLen;
  if (i >= bytes.length) return segs;
  const rcLen = byteAt(bytes, i);
  segs.push({ key: "c_len", label: `redemption_context length = ${rcLen}`, off: i, len: 1 });
  i += 1;
  if (rcLen > 0) {
    segs.push({ key: "redemption_context", label: "redemption_context", off: i, len: rcLen });
    i += rcLen;
  }
  if (i + 2 > bytes.length) return segs;
  const oiLen = (byteAt(bytes, i) << 8) | byteAt(bytes, i + 1);
  segs.push({ key: "c_len", label: `origin_info length = ${oiLen}`, off: i, len: 2 });
  i += 2;
  if (oiLen > 0) segs.push({ key: "origin_info", label: "origin_info", off: i, len: oiLen });
  return segs;
}

/** Fields of an ExtensionSet: a TLS vector of { required u8, type u16 }. */
function extSetSegments(bytes: Uint8Array): Segment[] {
  const segs: Segment[] = [];
  if (bytes.length < 2) return segs;
  segs.push({ key: "c_len", label: "extension_types vector length", off: 0, len: 2 });
  let i = 2;
  while (i + 3 <= bytes.length) {
    const req = byteAt(bytes, i);
    const type = (byteAt(bytes, i + 1) << 8) | byteAt(bytes, i + 2);
    segs.push({
      key: "es_required",
      label: `required = ${req === 1 ? "true" : req === 0 ? "false" : req}`,
      off: i,
      len: 1,
    });
    segs.push({ key: "es_type", label: `extension_type = ${type}`, off: i + 1, len: 2 });
    i += 3;
  }
  return segs;
}

/** A run of base64 characters that all belong to one field (or none). */
function solidSpan(text: string, seg: Segment | null | undefined): HTMLSpanElement {
  const c = colOf(seg);
  const span = document.createElement("span");
  span.className = "seg";
  span.style.background = c + "22";
  span.style.boxShadow = `inset 0 -2px 0 ${c}`;
  if (seg) span.title = `${seg.label}: bytes ${seg.off}..${seg.off + seg.len}`;
  span.textContent = text;
  return span;
}

/**
 * A single base64 char whose 6 bits straddle two fields: split gradient,
 * left = earlier field (high bits), right = later field (low bits).
 */
function gradientSpan(ch: string, a: Hit, b: Hit): HTMLSpanElement {
  const ca = colOf(a.s),
    cb = colOf(b.s);
  const p = Math.round((a.ov / (a.ov + b.ov)) * 100);
  const span = document.createElement("span");
  span.className = "seg";
  span.style.background = `linear-gradient(to right, ${ca}33 ${p}%, ${cb}33 ${p}%)`;
  span.style.boxShadow = `inset ${p}% -2px 0 ${ca}, inset ${p - 100}% -2px 0 ${cb}`;
  span.title = `field boundary: ${a.s.label} \u2192 ${b.s.label} (this base64 char encodes bits from both)`;
  span.textContent = ch;
  return span;
}

/**
 * Colour the base64 string itself, mapping byte-offset fields onto the
 * characters (6 bits each) that encode them.
 */
function buildB64View(b64: string, segs: Segment[]): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.className = "b64view";
  const chars = (b64 || "").replace(/\s+/g, "").split("");
  // Segments are byte-offset-ordered and non-overlapping; since we scan chars
  // left-to-right (bit windows only advance), walk `segs` with a pointer that
  // never rewinds. Each 6-bit window straddles at most one byte boundary
  // (fields are >=8 bits apart), so it touches at most two segments — keeping
  // the whole pass O(chars + segments) instead of O(chars * segments).
  const active = segs.filter((s) => s.len > 0);
  let si = 0;
  let run = "";
  let runSeg: Segment | null | undefined = undefined; // undefined = nothing buffered yet
  const flush = () => {
    if (run) wrap.appendChild(solidSpan(run, runSeg));
    run = "";
  };
  for (const [c, ch] of chars.entries()) {
    if (ch === "=") {
      flush();
      runSeg = undefined;
      const pad = document.createElement("span");
      pad.className = "pad";
      pad.textContent = ch;
      wrap.appendChild(pad);
      continue;
    }
    const bitStart = 6 * c,
      bitEnd = bitStart + 6;
    // Drop segments that end at or before this window; they can't recur.
    while (si < active.length) {
      const s = active[si];
      if (!s || (s.off + s.len) * 8 > bitStart) break;
      si++;
    }
    const hits: Hit[] = [];
    for (let k = si; k < active.length; k++) {
      const s = active[k];
      if (!s || s.off * 8 >= bitEnd) break; // this + later segments start past window
      const se = (s.off + s.len) * 8;
      const ov = Math.min(bitEnd, se) - Math.max(bitStart, s.off * 8);
      if (ov > 0) hits.push({ s, ov });
    }
    const [a, b] = hits;
    if (a && b) {
      flush();
      runSeg = undefined;
      wrap.appendChild(gradientSpan(ch, a, b));
    } else {
      const seg = a ? a.s : null;
      if (seg !== runSeg) {
        flush();
        runSeg = seg;
      }
      run += ch;
    }
  }
  flush();
  return wrap;
}

function buildLegend(items: Array<[string, string]>): HTMLDivElement {
  const l = document.createElement("div");
  l.className = "legend";
  for (const [key, label] of items) {
    const s = document.createElement("span");
    const i = document.createElement("i");
    i.style.background = FIELD_COLORS[key] ?? NEUTRAL;
    s.append(i, document.createTextNode(label));
    l.appendChild(s);
  }
  return l;
}

/** A colour swatch + label cell. */
function fieldName(key: string, label: string): HTMLTableCellElement {
  const td = document.createElement("td");
  const i = document.createElement("i");
  i.className = "sw";
  i.style.background = FIELD_COLORS[key] ?? NEUTRAL;
  td.append(i, document.createTextNode(label));
  return td;
}

/**
 * A label underlined/highlighted with a field colour, matching the
 * corresponding coloured bytes in the base64 view above.
 */
function tag(key: string, text: string): HTMLSpanElement {
  const c = FIELD_COLORS[key] || NEUTRAL;
  const span = document.createElement("span");
  span.textContent = text;
  span.style.background = c + "22";
  span.style.boxShadow = `inset 0 -2px 0 ${c}`;
  span.style.borderRadius = "3px";
  span.style.padding = "0.12rem 0.3rem";
  return span;
}

function valueCell(valueNode: Node | string): HTMLTableCellElement {
  const td = document.createElement("td");
  td.className = "mono";
  if (valueNode instanceof Node) td.appendChild(valueNode);
  else td.textContent = valueNode;
  return td;
}

function row(nameKey: string, nameLabel: string, valueNode: Node | string): HTMLTableRowElement {
  const tr = document.createElement("tr");
  tr.appendChild(fieldName(nameKey, nameLabel));
  tr.appendChild(valueCell(valueNode));
  return tr;
}

/** A row whose name cell is an arbitrary node (e.g. a coloured tag). */
function rowNode(nameNode: Node, valueNode: Node | string): HTMLTableRowElement {
  const tr = document.createElement("tr");
  const nameTd = document.createElement("td");
  nameTd.appendChild(nameNode);
  tr.appendChild(nameTd);
  tr.appendChild(valueCell(valueNode));
  return tr;
}

function fieldsTable(rows: HTMLTableRowElement[]): HTMLTableElement {
  const table = document.createElement("table");
  table.className = "fields";
  const body = document.createElement("tbody");
  body.append(...rows);
  table.appendChild(body);
  return table;
}

const hexU16 = (n: number): string => `0x${n.toString(16).padStart(4, "0")}`;

/** Decoded token fields, sourced from the server's parsed TokenInfo. */
function buildTokenTable(info: TokenInfo): HTMLTableElement {
  return fieldsTable([
    row("token_type", "token_type", hexU16(info.token_type)),
    row("nonce", "nonce", info.nonce || ""),
    row("challenge_digest", "challenge_digest", info.challenge_digest || ""),
    row("token_key_id", "token_key_id", info.token_key_id || ""),
    row("authenticator", "authenticator", `${info.authenticator_len} bytes (RSA blind signature)`),
  ]);
}

/** Decoded extension fields; null when there are no extensions. */
function buildExtTable(exts: ExtInfo[]): HTMLTableElement | null {
  if (!exts.length) return null;
  const rows: HTMLTableRowElement[] = [];
  for (const ext of exts) {
    let value: string;
    if (ext.extension_type === 1 && ext.expiry_unix != null) {
      const when = new Date(ext.expiry_unix * 1000).toISOString().replace(".000Z", "Z");
      value = `Expiration data: expires ${when} (${ext.expiry_unix}, precision ${ext.expiry_precision}s)`;
    } else {
      value = ext.data_hex ? `Hex data: 0x${ext.data_hex}` : "Hex data: (empty)";
    }
    rows.push(rowNode(tag("ext_type", `Extension type ${ext.extension_type}`), tag("ext_value", value)));
  }
  return fieldsTable(rows);
}

/** Decoded challenge fields, sourced from the server's parsed challenge. */
function buildChallengeTable(info: ChallengeInfo): HTMLTableElement {
  const origins = (info.origin_info || []).filter(Boolean);
  return fieldsTable([
    row("c_token_type", "token_type", hexU16(info.token_type)),
    row("issuer_name", "issuer_name", info.issuer_name || ""),
    row("origin_info", "origin_info", origins.length ? origins.join(", ") : "(any origin)"),
    row(
      "redemption_context",
      "redemption_context",
      info.redemption_context ? `0x${info.redemption_context}` : "(none)",
    ),
  ]);
}

/** Decoded extension-set entries; null when none are advertised. */
function buildExtSetTable(info: ChallengeInfo): HTMLTableElement | null {
  const set = info.extension_set || [];
  if (!set.length) return null;
  return fieldsTable(
    set.map((e) =>
      rowNode(
        tag("es_type", `Extension type ${e.extension_type}`),
        tag("es_required", e.required ? "required" : "optional"),
      ),
    ),
  );
}

function heading(text: string): HTMLHeadingElement {
  const h = document.createElement("h3");
  h.textContent = text;
  return h;
}

/** Main hook: render the coloured breakdown under Inspect token. */
export const renderTokenBreakdown: AfterHook = (body, section, p, ok) => {
  const bd = section.querySelector<HTMLElement>("[data-breakdown]");
  if (!bd) return;
  bd.innerHTML = "";
  bd.hidden = true;
  if (!ok || !isTokenInfo(body)) return;
  const info = body;

  const parsed = parseHeaderParams(p["token"] ?? "");
  const tokenBytes = b64bytes(parsed.token);
  if (tokenBytes.length < 98) return; // not a token we can lay out

  bd.appendChild(heading("Token"));
  bd.appendChild(
    buildLegend([
      ["token_type", "token_type"],
      ["nonce", "nonce"],
      ["challenge_digest", "challenge_digest"],
      ["token_key_id", "token_key_id"],
      ["authenticator", "authenticator"],
    ]),
  );
  bd.appendChild(buildB64View(parsed.token, tokenSegments(tokenBytes)));

  bd.appendChild(heading("Decoded token"));
  bd.appendChild(buildTokenTable(info));

  const extBytes = b64bytes(parsed.extensions);
  if (extBytes.length >= 2) {
    bd.appendChild(heading("Extensions"));
    bd.appendChild(
      buildLegend([
        ["ext_type", "extension_type"],
        ["ext_len", "length"],
        ["ext_value", "value"],
      ]),
    );
    bd.appendChild(buildB64View(parsed.extensions, extSegments(extBytes)));
  }

  const extTable = buildExtTable(info.extensions || []);
  if (extTable) {
    bd.appendChild(heading("Decoded extensions"));
    bd.appendChild(extTable);
  }
  bd.hidden = false;
};

/** Main hook: render the coloured breakdown under Inspect challenge. */
export const renderChallengeBreakdown: AfterHook = (body, section, p, ok) => {
  const bd = section.querySelector<HTMLElement>("[data-breakdown]");
  if (!bd) return;
  bd.innerHTML = "";
  bd.hidden = true;
  if (!ok || !isChallengeInfo(body)) return;
  const info = body;

  const parsed = parseChallengeParams(p["challenge"] ?? "");
  const chalBytes = b64bytes(parsed.challenge);
  if (chalBytes.length < 4) return; // not a challenge we can lay out

  bd.appendChild(heading("Challenge"));
  bd.appendChild(
    buildLegend([
      ["c_token_type", "token_type"],
      ["c_len", "length"],
      ["issuer_name", "issuer_name"],
      ["redemption_context", "redemption_context"],
      ["origin_info", "origin_info"],
    ]),
  );
  bd.appendChild(buildB64View(parsed.challenge, challengeSegments(chalBytes)));

  bd.appendChild(heading("Decoded challenge"));
  bd.appendChild(buildChallengeTable(info));

  if (info.challenge_digest) {
    bd.appendChild(heading("Challenge digest"));
    const note = document.createElement("p");
    note.className = "note";
    note.textContent =
      "SHA-256 of the challenge bytes above. " +
      "A token copies this into its challenge_digest field to bind itself to this challenge.";
    bd.appendChild(note);
    const dg = document.createElement("div");
    dg.className = "b64view";
    dg.appendChild(tag("digest", info.challenge_digest));
    bd.appendChild(dg);
  }

  // Advertised extension-set (highlight the blob when we have its base64).
  const esTable = buildExtSetTable(info);
  if (esTable) {
    const esBytes = b64bytes(parsed.extensionSet);
    if (esBytes.length >= 2) {
      bd.appendChild(heading("Extension set"));
      bd.appendChild(
        buildLegend([
          ["c_len", "length"],
          ["es_required", "required"],
          ["es_type", "extension_type"],
        ]),
      );
      bd.appendChild(buildB64View(parsed.extensionSet, extSetSegments(esBytes)));
    }
    bd.appendChild(heading("Decoded extension set"));
    bd.appendChild(esTable);
  }

  // Bound extensions, if the header carried any.
  const extBytes = b64bytes(parsed.extensions);
  if (extBytes.length >= 2) {
    bd.appendChild(heading("Extensions"));
    bd.appendChild(
      buildLegend([
        ["ext_type", "extension_type"],
        ["ext_len", "length"],
        ["ext_value", "value"],
      ]),
    );
    bd.appendChild(buildB64View(parsed.extensions, extSegments(extBytes)));
  }
  const extTable = buildExtTable(info.extensions || []);
  if (extTable) {
    bd.appendChild(heading("Decoded extensions"));
    bd.appendChild(extTable);
  }
  bd.hidden = false;
};
